import React from "react";
import { HelpCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { useT } from "../../context/LanguageContext";

// Stable testid suffixes for each FAQ row. These map 1:1 with the question
// ids below so QA / analytics can refer to a specific question reliably
// even when copy is translated. Order matches the spec's question list.
const QUESTION_KEYS = [
  "what",
  "cost",
  "trial",
  "noWebsite",
  "existingLeads",
  "quotes",
  "acceptOnline",
  "afterAccept",
  "aiReply",
  "notifications",
  "findCleaner",
  "recurring",
  "googleReviews",
  "pastCustomers",
  "finance",
  "growSection",
  "spanish",
  "noApp",
  "solo",
  "trialEnd",
];

export const LandingFAQ = () => {
  const { t } = useT();
  return (
    <section
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 w-full"
      data-testid="landing-faq"
    >
      <div className="text-center max-w-2xl mx-auto">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-50 border border-green-100 text-green-800 text-xs font-semibold tracking-wide">
          <HelpCircle className="w-3.5 h-3.5" />
          FAQ
        </span>
        <h2
          className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-gray-900 tracking-tight"
          data-testid="landing-faq-title"
        >
          {t("faq.title")}
        </h2>
        <p
          className="mt-4 text-gray-600 leading-relaxed text-base sm:text-lg"
          data-testid="landing-faq-subtitle"
        >
          {t("faq.subtitle")}
        </p>
      </div>

      <div className="mt-12 sm:mt-14 max-w-3xl mx-auto">
        <Accordion
          type="single"
          collapsible
          className="w-full space-y-3 sm:space-y-4"
        >
          {QUESTION_KEYS.map((key) => (
            <AccordionItem
              key={key}
              value={key}
              // Each FAQ row is its own card — gives the section a modern,
              // minimal SaaS feel and provides comfortable tap targets on
              // mobile. Removing the divider-only treatment also frees us
              // from coupling readability to the parent border-radius.
              className="border border-gray-200 rounded-2xl bg-white shadow-sm transition-shadow hover:shadow-md data-[state=open]:shadow-md data-[state=open]:border-green-200"
              data-testid={`landing-faq-item-${key}`}
            >
              <AccordionTrigger
                // Larger, more scannable question type. `hover:no-underline`
                // suppresses the default Radix underline so the row looks
                // like a polished SaaS card, not a text link.
                className="px-5 sm:px-7 py-5 sm:py-6 text-left text-base sm:text-lg font-medium text-gray-900 hover:no-underline hover:text-green-800 [&[data-state=open]]:text-green-800"
                data-testid={`landing-faq-trigger-${key}`}
              >
                {t(`faq.q.${key}`)}
              </AccordionTrigger>
              <AccordionContent
                // Answer body — slightly larger than before for desktop
                // readability while remaining comfortable on mobile.
                className="px-5 sm:px-7 pb-6 pt-0 text-[15px] sm:text-base text-gray-600 leading-relaxed"
                data-testid={`landing-faq-content-${key}`}
              >
                {t(`faq.a.${key}`)}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};
